import * as Sentry from "@sentry/browser";
import * as Configuration from '../app.constants.js';

httpInterceptorsConfig.$inject = ['$httpProvider'];
export function httpInterceptorsConfig($httpProvider) {


  // insert jwt token if we have one
  $httpProvider.interceptors.push(['$injector', function ($injector) {
    return {
      request: config => {
        let LoginFactory = $injector.get("LoginFactory");
        let jwt = LoginFactory.getJwt();
        if (jwt != null) {
          config.headers['Authorization'] = 'Bearer ' + jwt;
        }
        return config;
      }
    };
  }]);

  // Interceptor that retries exactly once GETs or whitelisted POSTS 
  // upon -1 statuses.
  $httpProvider.interceptors.push(['$injector', '$q', function ($injector, $q) {
    function retryOnce(response) {
      if (response.config.retryOngoing) {
        return $q.reject(response);
      }
      let $http = $injector.get('$http');
      response.config.retryOngoing = true;
      return $http(response.config);
    }
    return {
      responseError: response => {
        if (response.status !== -1) {
          return $q.reject(response);
        }
        if (response.config.method === "POST") {
          let postWhitelist = ["/tasks/lazyload$", "/tasks$", "/tasks/counts$"];
          for (let white of postWhitelist) {
            if (response.config.url.match(white)) {
              return retryOnce(response);
            }
          }
        }
        if (response.config.method === "GET") {
          return retryOnce(response);
        }
        return $q.reject(response);
      }
    }
  }]);

  // Interceptor that sends user_id+session_id to server to detect invalid client-side sessions
  $httpProvider.interceptors.push(['XHRStatsFactory', function (XHRStatsFactory) {
    return {
      'request': function (config) {
        config.headers['X-Alcmeon-Session'] = JSON.stringify({
          user_id: XHRStatsFactory.logguedUserId(),
          session_id: XHRStatsFactory.serverSessionId(),
          client_session_id: XHRStatsFactory.clientSessionId(),
          company_id: XHRStatsFactory.companyId()
        });
        return config;
      }
    };
  }]);


  // Interceptor that sends the last time we cleared local storage for debugging purposes
  $httpProvider.interceptors.push(['LocalStorageFactory', function (LocalStorageFactory) {
    return {
      'request': function (config) {
        config.headers['X-Alcmeon-Last-LS-Clear'] = JSON.stringify(LocalStorageFactory.getItem('last-ls-clear'));
        config.headers['X-Alcmeon-Last-LS-Clear-On-OQ'] = JSON.stringify(LocalStorageFactory.getItem('last-ls-clear-on-overquota'));
        return config;
      }
    };
  }]);

  // Interceptor that retries if we see a missing table
  $httpProvider.interceptors.push(['$q', '$injector', function ($q, $injector) {
    return {
      responseError: function (response) {
        if (response.status === 400 && response.data && response.data.error && response.data.error.reason && response.data.error.reason === 'invalid-table-name') {
          let $http = $injector.get('$http');
          return $http(response.config);
        } else {
          return $q.reject(response);
        }
      }
    };
  }]);

  // Interceptor that reloads the app if a request fails because the backends says our vesion is too old
  $httpProvider.interceptors.push(['$q', '$injector', function ($q, $injector) {
    return {
      request: function (config) {
        config.headers['X-Alcmeon-Client-Version'] = Configuration.constants.clientVersion;
        return config;
      },
      responseError: function (response) {
        if (response.status === 400 && response.data && response.data.error && response.data.error.reason && response.data.error.reason === 'client-version-mismatch') {
          // Client version is too old. Reload !
          let $window = $injector.get('$window');
          $window.location.reload();
        }
        return $q.reject(response);
      }
    };
  }]);

  // Interceptor that clears the local storage if a requests gets a 401 or 403 response
  // (but not if the error is due to an expired session)
  $httpProvider.interceptors.push(['$q', 'LocalStorageFactory', function ($q, LocalStorageFactory) {
    return {
      responseError: function (httpResponse) {
        if ((httpResponse.status === 401 || httpResponse.status === 403)
          && !(httpResponse.data
            && httpResponse.data.error
            && ['session-is-closed', 'auth-required-but-no-auth-present'].includes(httpResponse.data.error.reason))) {
          LocalStorageFactory.clear();
        }
        return $q.reject(httpResponse);
      }
    };
  }]);

  // Inteceptor that retries http requests if they fail because the current company is in maintenance mode 
  $httpProvider.interceptors.push(['$injector', '$q', function ($injector, $q) {
    return {
      responseError: function (rejection) {
        if (rejection.headers('X-Alcmeon-State') === 'maintenance' &&
          !rejection.config.url.endsWith('/users/self/notifications') &&
          !rejection.config.url.endsWith('/subscriptions')) {

          const maintenanceModeService = $injector.get('AngularMaintenanceModeService');
          return maintenanceModeService.maybeInitMaintenanceModeThenReturnFinishedObservable()
            .toPromise()
            .then(() => $injector.get('$http')(rejection.config));
        } else {
          return $q.reject(rejection);
        }
      }
    };
  }]);

  // Handle redirecting to an error page when an http error occurs
  $httpProvider.interceptors.push(['$q', '$injector', function ($q, $injector) {
    return {
      'responseError': function (error) {
        const $state = $injector.get('$state');
        const ErrorFactory = $injector.get('ErrorFactory');
        const AlertingFactory = $injector.get('AlertingFactory');

        if ($state.transition && !isWsSubscription(error)) { // do not redirect to login on 503 from ws subscription endpoint

          // If we are currently transitionning, it means that the error is happening in a router resolver
          // (except if a previous request was very slow, and the user changed state in the meantime,
          //  in which case it will probably be a -1 error, and we don't mind displaying an alert about it).

          if ([401, 403, 404, 500].includes(error.status)) {
            ErrorFactory.redirectToErrorPageFromToState(error);
          } else if (error.status === -1) {
            AlertingFactory.error('common/http_error/minus_one');
          } else {
            AlertingFactory.alertHTTPError(error);
          }
        } else {
          // If we are not currently transitionning, it means the error is on an endpoint called by a controller.
          // Hence we only redirect to an error or login page if it is a 401 or 403 error
          // (other errors are handled at the discretion of controllers, usually an alert is sufficient)
          if ([401, 403].includes(error.status) && ($state.current.name.startsWith('auth.'))) {
            ErrorFactory.redirectToErrorPage(error, $state.current.name, $state.params);
          }
        }
        return $q.reject(error);
      }
    }
  }]);

  /**
   * Interceptor that handles api calls that failed and forward to Sentry.
   */
  $httpProvider.interceptors.push(['$q', function ($q) {
    return {
      responseError: function (response) {
        Sentry.captureException(response)
        return $q.reject(response);
      }
    };
  }]);

}

function isWsSubscription(error) {
  return error.config
    && error.config.url
    && error.config.url.endsWith('/subscriptions');
}